import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';

export interface ContactMessage {
  name: string;
  email: string;
  message: string;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = 'https://api.your-portfolio.com'; // URL який ми плануємо для бекенду

  constructor() { }

  // Імітація відправки повідомлення контактної форми
  sendContactMessage(data: ContactMessage): Observable<ApiResponse> {
    console.log('Simulating API call to:', `${this.baseUrl}/api/contact`);
    console.log('Contact data:', data);

    // Імітація затримки мережі
    return of(null).pipe(
      delay(2000), // 2 секунди затримки для реалістичності
      switchMap(() => {
        // Імітація успішної або неуспішної відповіді (90% успіху)
        if (Math.random() > 0.1) {
          return of({
            success: true,
            message: 'Message sent successfully!',
            data: {
              id: Math.random().toString(36).substr(2, 9),
              timestamp: new Date().toISOString()
            }
          });
        } else {
          return throwError(() => ({
            success: false,
            message: 'Failed to send message. Please try again.',
            error: 'Network error'
          }));
        }
      })
    );
  }

  // Імітація завантаження даних портфоліо
  getPortfolioData(): Observable<any> {
    console.log('Simulating API call to:', `${this.baseUrl}/api/portfolio`);

    const portfolioData = {
      profile: {
        name: 'Your Name',
        title: 'Full-Stack Developer',
        bio: 'Passionate developer with expertise in modern web technologies',
        avatar: '/assets/profile.png',
        location: 'Lviv, Ukraine',
        experience: '5+'
      },
      skills: [
        'Angular', 'React', 'Vue.js', 'TypeScript', 'JavaScript',
        'Node.js', '.NET Core', 'Python', 'PostgreSQL', 'MongoDB',
        'Docker', 'AWS', 'Azure', 'Git', 'Bootstrap', 'Tailwind CSS'
      ],
      projects: [
        {
          id: 1,
          title: 'E-commerce Platform',
          description: 'Full-stack e-commerce solution with Angular and .NET Core',
          image: '/assets/abstract_code.png',
          technologies: ['Angular', '.NET Core', 'PostgreSQL', 'Bootstrap'],
          demoUrl: '#',
          githubUrl: '#'
        },
        {
          id: 2,
          title: 'Task Management App',
          description: 'Collaborative task management tool with real-time updates',
          image: '/assets/teamwork.png',
          technologies: ['React', 'Node.js', 'Socket.io', 'MongoDB'],
          demoUrl: '#',
          githubUrl: '#'
        },
        {
          id: 3,
          title: 'Analytics Dashboard',
          description: 'Data visualization dashboard for business analytics',
          image: '/assets/lviv_silhouette.png',
          technologies: ['Vue.js', 'Python', 'D3.js', 'PostgreSQL'],
          demoUrl: '#',
          githubUrl: '#'
        }
      ],
      testimonials: [
        {
          id: 1,
          name: 'John Smith',
          position: 'CEO at TechCorp',
          text: 'Excellent work! The team showed high professionalism and delivered beyond expectations.',
          avatar: '/assets/profile.png',
          rating: 5
        },
        {
          id: 2,
          name: 'Maria Garcia',
          position: 'Product Manager at StartupXYZ',
          text: 'Very pleased with the quality of work and timely delivery. Highly recommended!',
          avatar: '/assets/profile.png',
          rating: 5
        }
      ]
    };

    return of(portfolioData).pipe(delay(1000));
  }

  // Імітація підписки на newsletter
  subscribeToNewsletter(email: string): Observable<ApiResponse> {
    console.log('Simulating API call to:', `${this.baseUrl}/api/newsletter/subscribe`);
    console.log('Email:', email);

    return of({
      success: true,
      message: 'Successfully subscribed to newsletter!',
      data: { email, subscribedAt: new Date().toISOString() }
    }).pipe(delay(1500));
  }

  // Імітація завантаження блог постів (для майбутнього розширення)
  getBlogPosts(): Observable<any[]> {
    console.log('Simulating API call to:', `${this.baseUrl}/api/blog`);

    const blogPosts = [
      {
        id: 1,
        title: 'Getting Started with Angular 17',
        excerpt: 'Learn the new features and improvements in Angular 17',
        content: 'Full blog post content...',
        publishedAt: '2024-01-15',
        tags: ['Angular', 'TypeScript', 'Web Development']
      },
      {
        id: 2,
        title: 'Building APIs with .NET Core',
        excerpt: 'Best practices for creating robust APIs',
        content: 'Full blog post content...',
        publishedAt: '2024-01-10',
        tags: ['.NET Core', 'API', 'Backend']
      }
    ];

    return of(blogPosts).pipe(delay(800));
  }
}
